import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { ViewAction, DeleteAction } from "@/components/table/actions";
import {
  useDeleteProjectAttachmentMutation,
  useGetAllProjectAttachmentsQuery,
} from "@/services/(company)/api.projects";
import { useMemo, useState } from "react";
import { PROJECT_TAB_KEYS } from "@/routes/company/routes.projects.constants";
import { useProjectTabParams } from "../params";
import { Button } from "@/components/ui/button";
import { Download, Paperclip } from "lucide-react";
import type { ISingleProjectAttachment } from "@/types/(company)/projects.interface";
import { Table, type ColumnDef } from "@/components/table";
import { useNavigate } from "react-router-dom";
import { startCase } from "lodash";
import toast from "react-hot-toast";
import { useConfirm } from "@/context/Confirm";
import { UploadProjectAttachmentDialog } from "./upload-project-attachment-dialog";
import { downloadFile } from "@/utils";
import { COMPANY_PROJECTS_PATHS } from "@/routes/company/routes.projects";
import { AttachmentCell } from "@/pages/(home)/_components/document-request-shared";
import { useFilePreview } from "@/context/ImagePreview";
import { buildFilePresignedUrl } from "@/utils/file.utils";
import { usePermissions } from "@/hooks/usePermissions";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { useLocale } from "@/hooks/useLocale";

const buildColumns = (t: TFunction): ColumnDef<Record<string, unknown>>[] => [
  {
    key: "preview",
    label: t("projects:preview"),
  },
  {
    key: "name",
    label: t("projects:attachment_name"),
  },
  {
    key: "type",
    label: t("common:labels.type"),
  },
  {
    key: "createdAt",
    label: t("projects:uploaded_on"),
  },
  {
    key: "actions",
    label: t("projects:actions"),
  },
];

export const ProjectAttachmentsTable = ({
  projectId,
}: {
  projectId: string;
}) => {
  const navigate = useNavigate();
  const openFile = useFilePreview();
  const { can } = usePermissions();
  // Search and page live in the URL, namespaced per tab — see useTableParams.
  const tp = useProjectTabParams(PROJECT_TAB_KEYS.Attachements);
  const { t } = useTranslation(["projects", "common"]);
  const { formatDate } = useLocale();

  const { data: { data: attachments, meta } = {}, isFetching } =
    useGetAllProjectAttachmentsQuery({
      projectId,
      // Spread, not inline keys: this endpoint takes `pageSize`, which the
      // shared `IApiGeneralParams` arg type doesn't declare.
      params: {
        page: tp.page,
        search: tp.committedSearch,
        ...({ pageSize: 10 } as Record<string, number>),
      },
    });

  const attachmentById = useMemo(
    () => new Map(attachments?.map((a) => [a.id, a])),
    [attachments],
  );

  const rows =
    attachments?.map((attachment) => {
      const presignedUrl = buildFilePresignedUrl(attachment.fileId);
      return {
        id: attachment.id,
        preview: (
          <AttachmentCell
            url={attachment.fileUrl || presignedUrl}
            onPreview={() => openFile(presignedUrl)}
            noRowClick
          />
        ),
        name: startCase(attachment.name.split(".")[0]),
        type: attachment.fileType,
        createdAt: formatDate(new Date(attachment.createdAt), "dd MMM yyyy"),
        actions: (
          <RowActions
            projectId={projectId}
            attachment={attachment}
            onPreview={() => openFile(presignedUrl)}
          />
        ),
      };
    }) ?? [];

  return (
    <Table
      data={rows}
      loading={isFetching}
      searchText={tp.search}
      columns={buildColumns(t)}
      noPadding
      pagination={{
        totalPages: meta?.totalPages ?? 1,
        currentPage: tp.page,
        onPageChange: tp.setPage,
      }}
      onSearchChange={tp.setSearch}
      searchPlaceholder={t("projects:search_by_team_name")}
      actions={[
        ...(can(PLATFORM_SECTION_ENUM.PROJECTS, "create")
          ? [
              {
                type: "custom" as const,
                key: "upload",
                render: () => (
                  <UploadProjectAttachmentDialog>
                    <Button variant="primary" className="gap-2 text-sm!">
                      <span className="bg-white rounded-xs w-fit p-[2px] md:p-1">
                        <Paperclip className="size-4 text-primary font-medium" />
                      </span>
                      {t("projects:upload_new")}
                    </Button>
                  </UploadProjectAttachmentDialog>
                ),
              },
            ]
          : []),
      ]}
      onRowClick={(row) => {
        const att = attachmentById.get(row.id as number);
        if (att)
          navigate(
            COMPANY_PROJECTS_PATHS.VIEW_ATTACHMENT(att.projectId, att.id),
            { state: { attachment: att } },
          );
      }}
    />
  );
};

const RowActions = ({
  projectId,
  attachment,
  onPreview,
}: {
  projectId: string;
  attachment: ISingleProjectAttachment;
  onPreview: () => void;
}) => {
  const { t } = useTranslation(["projects", "common"]);
  const confirm = useConfirm();
  const { can } = usePermissions();
  const [isDownloading, setIsDownloading] = useState(false);
  const [deleteFile, { isLoading: isDeleting }] =
    useDeleteProjectAttachmentMutation();

  const handleDeleteFile = async (attachmentId: string) => {
    confirm({
      title: t("projects:delete_aattachment"),
      msg: t("projects:are_you_sure_you_want_to_delete_this_attachment"),
      onConfirm: async () => {
        await deleteFile({ projectId, attachmentId })
          .unwrap()
          .then((res) => toast.success(res.message))
          .catch((e) => toast.error(e?.data?.message ?? e?.message));
      },
    });
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    await downloadFile(attachment.fileUrl, attachment.name);
    setIsDownloading(false);
  };

  return (
    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
      <ViewAction
        tooltip={t("common:shared_ui.view_attachment")}
        onClick={onPreview}
      />
      <Button
        size="icon"
        aria-label={t("projects:download_attachment")}
        data-tooltip-id="hrm-tooltip"
        data-tooltip-content={t("projects:download_attachment")}
        variant="outline-primary"
        disabled={isDownloading}
        onClick={handleDownload}
      >
        <Download size={16} />
      </Button>
      <DeleteAction
        tooltip={t("projects:delete_attachment")}
        disabled={isDeleting || !can(PLATFORM_SECTION_ENUM.PROJECTS, "delete")}
        onClick={() => handleDeleteFile(attachment.id.toString())}
      />
    </div>
  );
};
